import { Overlay, OverlayConfig, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ENG_STATUS_ID, LukaInputUtil } from 'countable@helpers';
import { AiActivationInfo, LucaEngListModel, LucaTermsInfo, LucaTermsModel, OverlayInputInterface, pageInfo } from 'countable@model';
import { AiService } from 'countable@services';
import { distinctUntilChanged } from 'rxjs/operators';
import { EngService } from '../../../services/eng.service';
import { LukaHowItWorksComponent } from '../../luka-bot/luka-how-it-works/luka-how-it-works.component';
import { LukaOverlayComponent } from '../../luka-bot/luka-overlay/luka-overlay.component';

@Component({
  selector: 'luka-launcher',
  templateUrl: './luka-launcher.component.html',
  styleUrl: './luka-launcher.component.scss',
  imports: [OverlayModule, PortalModule, LukaOverlayComponent, NgClass, MatButton, MatCheckbox, MatDialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})

export class LukaLauncherComponent implements OnInit {

  @ViewChild(CdkPortal) portal!: CdkPortal;
  @ViewChild('termsDialog') termsDialog: TemplateRef<any>;
  @ViewChild('aiActivatedDialog') aiActivatedDialog: TemplateRef<any>;
  private readonly lukUtil: typeof LukaInputUtil = LukaInputUtil;
  private readonly engStatusId: typeof ENG_STATUS_ID = ENG_STATUS_ID;
  private overlayRef: OverlayRef;
  botInputs: WritableSignal<OverlayInputInterface> = signal<OverlayInputInterface>(null);
  isLukaMinimized: WritableSignal<boolean> = signal<boolean>(false);
  lucaInfo: WritableSignal<LucaTermsModel> = signal<LucaTermsModel>(null);
  aiDisplayInfo: WritableSignal<string[]> = signal<string[]>([]);
  private activeChatId: WritableSignal<string> = signal<string>(null);

  constructor(private overlay: Overlay, private router: Router, private ai: AiService, private engService: EngService
  ,private dialog: MatDialog) {
    this.overlayRef && this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef.detach();
    });

    this.getPromptsList();
    this.getThreads();
    this.getAllEngagementsList();

    window.addEventListener('message', (event) => {
      if (event.data?.action === 'openLuka') {
        this.activeChatId.set(event.data?.activeChatId);
        this.openLuka(true);
      }
    });
  }

  ngOnInit(): void {
    this.engService.refreshAiEngagements$.pipe(
      // takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: res => {
        if (res === true) {
          this.getAllEngagementsList();
        }
      }
    });

    this.engService.refreshSpecificEng$.pipe(
      // takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: engId => {
        if (engId > 0) {
          this.updateSpecificEngagement();
        }
      }
    });
  }

  public openLuka(isFromWindow: boolean = false) {
    this.overlayRef = null;
    this.botInputs.set({
      activeChatId: isFromWindow ? this.activeChatId() : (this.isLukaMinimized() ? this.activeChatId() : null),
      pageInformation: isFromWindow ? null : (!this.isLukaMinimized() ? this.catchClientInfo(this.router.url): null),
      isMinimized: isFromWindow ? true : this.isLukaMinimized()
    } as OverlayInputInterface);
    const config = new OverlayConfig({
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      width: '60%',
      hasBackdrop: true
    });
    this.overlayRef = this.overlay.create(config);
    this.overlayRef.attach(this.portal);
    this.isLukaMinimized.set(false);
    this.getPromptsList();
    this.getThreads();
  }

  executeOverlayAction(event: { type: string, chatId: string }) {
    console.log('executeOverlayAction', event);
    if (event?.type === 'MINIMIZE') {
      this.isLukaMinimized.set(true);
      this.activeChatId.set(event?.chatId);
    } else {
      this.isLukaMinimized.set(false);
      this.activeChatId.set(null);
    }
    this.overlayRef.detach();
  }

  public catchClientInfo(url: string) {
    const clientObj: pageInfo = new pageInfo();
    if (url.includes('client-engagements')) {
      const clientInfo = JSON.parse(localStorage.getItem('selectedClientDetails'));
      clientObj.clientId = clientInfo?.clientfirmId ?? clientInfo?.clientfirmid;
      clientObj.clientName = clientInfo?.cleintLegalCorporationName;
      clientObj.pageType = 'CLIENT';
    } else if (url.includes('create-engagement/1')) {
      const clientInfo = JSON.parse(localStorage.getItem('selectedClientDetails'));
      clientObj.clientId = clientInfo?.clientfirmid;
      clientObj.clientName = clientInfo?.businessname ? clientInfo?.businessname : clientInfo?.clientName;
      clientObj.pageType = 'CLIENT';
    } else if (url.includes('/create-engagement/2')) {
      const engInfo = JSON.parse(localStorage.getItem('selectedEngagementObj'));
      clientObj.clientId = engInfo?.clientfirmid;
      clientObj.clientName = engInfo?.businessname ? engInfo?.businessname : engInfo?.clientName;
      clientObj.pageType = 'ENGAGEMENT';
      clientObj.engName = engInfo?.engagementName ?? engInfo?.engagementname;
      clientObj.engId = engInfo?.engagementsid ?? 0;
    } else if (url.includes('create-engagement/3')) {
    } else if (url.includes('/dashboard/ntrdashboard/')) {
      const engInfo = JSON.parse(localStorage.getItem('selectedEngagementObj'));
      clientObj.clientId = engInfo?.clientfirmid;
      clientObj.clientName = engInfo?.clientName;
      clientObj.pageType = 'ENGAGEMENT';
      clientObj.engName = engInfo?.engagementName ?? engInfo?.engagementname;
      clientObj.engId = engInfo?.engagementsid ?? 0;
    } else if (url.endsWith('/clients')) {
    } else {
      clientObj.pageType = 'NONE';
    }
    return clientObj;
  }

  getPromptsList() {
    this.ai.fetchPrompts().pipe().subscribe({
      next: res => {
        if (res?.status === 200 && res?.data?.length > 0) {
          this.ai.prompts$.next(res?.data);
        }
      }
    });
  }

  getThreads() {
    this.ai.fetchThreads().pipe().subscribe({
      next: res => {
        if (res?.status === 200 && res?.data?.length) {
          this.ai.threads$.next(this.lukUtil.sortThreads(res?.data));
        }
      }
    });
  }

  private getAllEngagementsList() {
    this.engService.getAllEngagementsList('All').pipe(
      distinctUntilChanged()
      // takeUntilDestroyed(this.destroyRef)
      // map(res => this.filterEngagementsList(structuredClone(res)))
    ).subscribe(response => {
      if (response) {
        const engList = this.engService.getRefinedAllEngagementsList(structuredClone(response), '');
        if (engList?.firmEngagementsList) {
          const refinedEngList = this.filterEngagementsList(structuredClone(engList?.firmEngagementsList));
          this.engService.aiEngagements.next([...refinedEngList]);
        } else {
          this.engService.aiEngagements.next([]);
        }
      }
    });
  }

  private filterEngagementsList(engList: any[] = []): LucaEngListModel[] | [] {
    if (engList?.length === 0) {
      return [];
    }
    return engList?.map(e => <LucaEngListModel>{
      clientId: e?.clientfirmid,
      clientName: e?.businessname ? e?.businessname : e?.clientName,
      engName: e?.engagementname ? e?.engagementname : e?.engagementName,
      engId: e?.engagementsid,
      yearEnd: e?.yearEnd,
      engCreatedDate: e?.dateCreated,
      engStatus: e?.status,
      engStatusId: e?.statusid,
      engType: e?.engagementtype,
      year: this.getYear(e?.yearEnd),
      engTypeId: e?.pengagementtypeid,
      engYearEnd: e?.financialYearEnd,
      isRestricted: ![this.engStatusId.ENG_NEW, this.engStatusId.ENG_IN_PROGRESS, this.engStatusId.ENG_RE_OPEN].includes(e?.statusid)
    });
  }

  private getYear = (dateString: string): string => {
    const date = new Date(dateString);
    return date.getFullYear().toString();
  };

  private updateSpecificEngagement(engId: number = 0) {
    const engList: LucaEngListModel[] = this.engService.aiEngagements.getValue();
    const eng = engList?.find(e => e?.engId === engId);
    if (eng?.engId > 0) {
      eng.engStatusId = 7;
    }
    this.engService.aiEngagements.next(engList);
  }

  openTerms() {
    const info: LucaTermsModel = new LucaTermsModel();
    info.terms = structuredClone(LucaTermsInfo);
    info.isChecked = false;
    info.updatedOn = 'Last updated June 01,2024';
    info.title = 'Luka - Terms & Conditions';
    info.showTermsOnly = false;
    this.lucaInfo.set(info);
    const dialogRef = this.dialog.open(this.termsDialog);
    dialogRef.afterClosed().subscribe({
      next: res => {
        console.log('Response For Closing the Dialog', res);
        if (res) {
          // Write Logic For an API Call to fetch the Details of the Step-1
          // this.updateActivateAddons(type);
          this.activatedConfirmationDialog();
        }
      }
    });
  }

  changeTerms(event: any) {
    const info: LucaTermsModel = structuredClone(this.lucaInfo());
    info.isChecked = event?.checked;
    this.lucaInfo.set(info);
  }

  public activatedConfirmationDialog() {
    this.aiDisplayInfo.set(structuredClone(AiActivationInfo));
    this.dialog.open(this.aiActivatedDialog).afterClosed().subscribe({
      next: res => {
        if (res) {

        }
      }
    });
  }

  openHowItWorks() {
    this.dialog.closeAll();
    this.dialog.open(LukaHowItWorksComponent).afterClosed().subscribe({
      next: res => {
        if (res) {
          // this.router.navigate([], {relativeTo: this.activatedRoute}).then();
        }
      }
    });
  }



}
